import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ChevronRight, FileText, Flame, ShieldCheck, Truck, Users } from "lucide-react";
import { PHOTOS } from "../lib/brand";
import { useQuote } from "../context/QuoteContext";
import { useProducts } from "../hooks/useProducts";
import { ProductCard } from "../components/site/ProductCard";

const SERVICES = [
  { title: "Fire Truck Manufacturing", copy: "Water, foam, ARFF and rescue tenders engineered for demanding public-sector response.", icon: Truck, to: "/vehicles" },
  { title: "Specialized Equipment", copy: "Trailer mist systems, fire bikes, portable pumps and hydrant packages.", icon: Flame, to: "/products" },
  { title: "Fire Manpower & Staffing", copy: "Certified crews, station staffing and safety audits for industrial sites.", icon: Users, to: "/services" },
  { title: "GeM & E-Tender Support", copy: "End-to-end technical and commercial support for compliant procurement.", icon: FileText, to: "/tenders" },
];

function Hero() {
  const { openQuote } = useQuote();
  return (
    <section className="hero">
      <img src={PHOTOS.hero} alt="Red emergency fire vehicle" />
      <div className="hero-overlay" />
      <div className="wrap hero-content">
        <div className="hero-copy reveal">
          <p className="eyebrow light">Engineered for emergency readiness</p>
          <h1>Fire response, built for the moments that matter.</h1>
          <p className="hero-sub">Government-approved fire trucks and emergency vehicles designed, equipped and delivered for India’s most demanding missions.</p>
          <div className="hero-actions">
            <button className="button button-orange" onClick={() => openQuote({ source: "hero" })} data-testid="hero-quote-button">Get an instant quote <ArrowRight size={17} /></button>
            <Link className="button button-outline" to="/products" data-testid="hero-vehicles-link">Explore 15 products</Link>
          </div>
          <div className="trust-row"><span><ShieldCheck /> GeM Registered OEM</span><span><CheckCircle2 /> ISO 9001:2015</span><span><Flame /> Make in India</span></div>
        </div>
      </div>
    </section>
  );
}

const GlassServices = () => (
  <section className="glass-section" style={{ backgroundImage: `url(${PHOTOS.fleet})` }}>
    <div className="glass-section-overlay" />
    <div className="wrap glass-section-inner">
      <div className="section-head light">
        <div><p className="eyebrow light">What we do</p><h2>Response systems, not just vehicles.</h2></div>
        <p className="section-lead">From a single rescue pickup to a complete fire station setup, ML FAB aligns equipment, documentation and people around your mission.</p>
      </div>
      <div className="glass-grid">
        {SERVICES.map(({ title, copy, icon: Icon, to }, i) => (
          <article className="glass-card" key={title} style={{ animationDelay: `${i * 90}ms` }} data-testid={`service-card-${i}`}>
            <span className="glass-icon"><Icon /></span><span className="service-index">0{i + 1}</span>
            <h3>{title}</h3><p>{copy}</p>
            <Link to={to} data-testid={`service-link-${i}`}>Explore capability <ChevronRight size={15} /></Link>
          </article>
        ))}
      </div>
    </div>
  </section>
);

function Featured() {
  const products = useProducts();
  return (
    <section className="section wrap">
      <div className="section-head">
        <div><p className="eyebrow">Field-ready range</p><h2>Vehicles with a job to do.</h2></div>
        <Link className="text-link" to="/products" data-testid="all-vehicles-link">View full catalog <ArrowRight size={16} /></Link>
      </div>
      <div className="pgrid">{products.slice(0, 6).map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}</div>
    </section>
  );
}

const Delivered = () => (
  <section className="section slate">
    <div className="wrap delivered">
      <div><p className="eyebrow">Recently delivered</p><h2>On the road with state fire services.</h2><p className="section-lead">Real ML FAB builds in service with fire &amp; emergency departments, renewable power sites and industrial clients.</p></div>
      <div className="delivered-grid">{[PHOTOS.fleet, PHOTOS.delivered, PHOTOS.mist, PHOTOS.pump].map((src, i) => <img key={src} src={src} alt="ML FAB delivered vehicle" data-testid={`delivered-photo-${i}`} />)}</div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <section className="signal-band"><div className="wrap signal-grid">
        <div><span className="big-number">15</span><strong>Catalogued platforms</strong><p>Vehicles, mobile systems and equipment with downloadable spec sheets.</p></div>
        <div><span className="big-number">24h</span><strong>Fast technical response</strong><p>Speak to a specialist within one working day of your enquiry.</p></div>
        <div><span className="big-number">360°</span><strong>One accountable partner</strong><p>From chassis integration to crew and after-sales support.</p></div>
      </div></section>
      <GlassServices />
      <Featured />
      <section className="dark-band"><div className="wrap split-promo">
        <div><p className="eyebrow light">Tender confidence</p><h2>Procurement support that speaks your language.</h2><p>Technical compliance, GeM listing guidance and bid-ready documentation — handled by a team that understands fire safety equipment.</p><Link to="/tenders" className="button button-white" data-testid="tender-support-link">See tender support <ArrowRight size={17} /></Link></div>
        <div className="quote-mark">“<span>When readiness is the brief, details are the difference.</span>”</div>
      </div></section>
      <Delivered />
    </>
  );
}
